import * as React from 'react';

import { useTranslation } from 'react-i18next';
import { ProfileDocumentFragment, TravellerProfileFragment } from '@websky/graphql';
import { MobileDialog } from '@shared/ui';

import { useFormState } from 'react-final-form';

import { useTheme } from '../../../../theme';
import { BaseButton } from '../../../../BaseComponents';
import { CdnIcon } from '@shared/ui';
import { useDocumentFields } from '../DocumentFieldsItem/useDocumentFields';
import DocumentFormFields from '../DocumentFormFields/DocumentFormFields';

interface Props {
	isOpen: boolean;
	isEdit?: boolean;
	index: number;
	validators?: ProfileDocumentFragment;
	traveller: TravellerProfileFragment;
	primaryCountryCodes?: string[];
	onClose: () => void;
	onSave: () => void;
	onClear?: (index: number) => void;
}

const AddDocumentModal: React.FC<Props> = ({
	isOpen,
	index,
	validators,
	traveller,
	primaryCountryCodes,
	onClose,
	onSave,
	onClear,
	isEdit
}) => {
	const { AddDocumentModal: css } = useTheme('TravellerForm');

	const { t: tTraveller } = useTranslation('Traveller');

	const { values } = useFormState({ subscription: { values: true } });

	const { documentFields, buildSaveHandler } = useDocumentFields(index, validators, traveller, primaryCountryCodes);

	const handleSave = buildSaveHandler(onSave);

	const hasAnyFieldFilled = documentFields.some(field => !!values?.documents?.[index]?.[field]);

	const footer = (
		<div className={css.addDocumentModal__footer}>
			<BaseButton variant="smallSecondary" className={css.addDocumentModal__cancelButton} onClick={onClose}>
				{tTraveller('Cancel')}
			</BaseButton>
			<BaseButton variant="smallPrimary" className={css.addDocumentModal__saveButton} onClick={handleSave}>
				{isEdit ? tTraveller('Save') : tTraveller('Add')}
			</BaseButton>
		</div>
	);

	return (
		<MobileDialog
			title={isEdit ? tTraveller('Edit document') : tTraveller('Add document')}
			isOpen={isOpen}
			onClose={onClose}
			footer={footer}
			className={css.addDocumentModal__modalContent}
			classes={{
				mobileDialog__header: css.addDocumentModal__modalHeader
			}}
		>
			<div className={css.addDocumentModal__content}>
				<DocumentFormFields
					index={index}
					validators={validators}
					traveller={traveller}
					primaryCountryCodes={primaryCountryCodes}
					variant="underline"
					layout="stack"
				/>
			</div>
			{isEdit && onClear && hasAnyFieldFilled && (
				<BaseButton className={css.addDocumentModal__clearButton} onClick={() => onClear(index)}>
					<CdnIcon icon="Action/Close-Clear" />
					{tTraveller('Clear')}
				</BaseButton>
			)}
		</MobileDialog>
	);
};

export default AddDocumentModal;
